// src/components/acciones/BoletaFinalPreview.js
import React, { memo, useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import BoletaPreview from './BoletaPreview'; 
import { CenteredSvg, buildCuponSvgUrl, inferCuponVariantCode } from './CuponesPreview';

// =========================================================================
// 🛠️ CONFIGURACIÓN DE PRECISIÓN (17x11 TABLOIDE)
// =========================================================================
const WORKSPACE_W = 1632; 
// Constante `WORKSPACE_H` que centraliza un valor de configuración compartido.
const WORKSPACE_H = 1056; 

// 🚩 CONFIGURACIÓN DE LÍNEA DE RECORTE INDEPENDIENTE
const CUT_LINE_X_V = 59; // Posición para arreglo VERTICAL (%)
// Constante `CUT_LINE_X_H` que centraliza un valor de configuración compartido.
const CUT_LINE_X_H = 62.5; // Posición para arreglo HORIZONTAL (%)

// =========================================================================
// 🛡️ NUDGE (EMPUJE) MANUAL DEL ESCUDO - SOLO PARA IMPRESIÓN
// =========================================================================
// Ahora que ambas caras tienen la misma orientación, el escudo debería
// estar perfectamente centrado. Si por tu impresora necesitas moverlo
// unos milímetros, puedes usar estos valores (0 = centrado perfecto).
const PRINT_SHIELD_ADJUST_H = { X: 0, Y: -70 }; 
// Constante `PRINT_SHIELD_ADJUST_V` que centraliza un valor de configuración compartido.
const PRINT_SHIELD_ADJUST_V = { X: 0, Y: 0 }; 

// 🖨️ CONFIGURACIÓN ORIGINAL DE IMPRESIÓN
const CONFIG_V = {
  BOLETA_X: -75,   
  BOLETA_Y: 0,    
  CUPONES_X: -105, 
  CUPONES_Y: -40,
  SCALE_V: 1.7,  
  SCALE_H: 1.9,
  BOLETA_ROT: 0,
  CUPON_SCALE: 1.52,
  CUPON_ROT: 0,
  GRID: { cols: 3, rows: 10, columnGap: 65, rowGap: 35, paddingX: 0, paddingY: 75 }
};

// Constante `CONFIG_H` que centraliza un valor de configuración compartido.
const CONFIG_H = {
  BOLETA_X: -70,   
  BOLETA_Y: 0,    
  SCALE_V: 2,   
  SCALE_H: 1.75,   
  BOLETA_ROT: -90, 
  CUPONES_X: -35,  
  CUPONES_Y: 0,
  CUPON_SCALE: 2.8, 
  CUPON_ROT: -90,  
  GRID: { cols: 5, rows: 6, columnGap: 45, rowGap: 105, paddingX: 10, paddingY: 60 }
};

// Constante `CANONICAL` que centraliza un valor de configuración compartido.
const CANONICAL = { 
  vertical: { width: 450, height: 670 }, 
  horizontal: { width: 670, height: 450 } 
};

// Constante `LOGO_TO_CUPON_ICON` que centraliza un valor de configuración compartido.
const LOGO_TO_CUPON_ICON = { 1: '1_C.svg', 2: '23_C.svg', 3: '23_C.svg', 4: '4_C.svg', 5: '5_C.svg' };

// Función `resolveIconUrl` que calcula el resultado necesario para el flujo.
function resolveIconUrl(selectedLogoNumber) {
  // Constante `CUPONES_BASE` que centraliza un valor de configuración compartido.
  const CUPONES_BASE = (process.env.PUBLIC_URL || '') + '/designs/cupones';
  // Valor intermedio `num` utilizado por la lógica de este bloque.
  const num = Number(selectedLogoNumber);
  // Valor intermedio `fileName` utilizado por la lógica de este bloque.
  const fileName = LOGO_TO_CUPON_ICON[num];
  return fileName ? `${CUPONES_BASE}/${fileName}` : null;
}

// Función `buildTransform` que construye la estructura o el valor requerido.
const buildTransform = (shiftX, shiftY, rotation, scaleV, scaleH) => {
  // Valor intermedio `signX` utilizado por la lógica de este bloque.
  const signX = shiftX >= 0 ? '+' : '-';
  // Valor intermedio `signY` utilizado por la lógica de este bloque.
  const signY = shiftY >= 0 ? '+' : '-';
  return `translate(calc(-50% ${signX} ${Math.abs(shiftX)}px), calc(-50% ${signY} ${Math.abs(shiftY)}px)) rotate(${rotation}deg) scale(${scaleH}, ${scaleV})`;
};

// =========================================================================
// 🧩 COMPONENTE MATRIZ DE CUPONES
// =========================================================================
const CuponesMatrix = memo(function CuponesMatrix({ 
  availableWidth, availableHeight, svgUrl, iconUrl, 
  config, startCode = 0, offsetX = 0, offsetY = 0 
}) {
  // Valores extraídos de una estructura para usarlos directamente en este bloque.
  const { cols, rows, columnGap, rowGap, paddingX, paddingY } = config.GRID;
  // Valor intermedio `innerW` utilizado por la lógica de este bloque.
  const innerW = availableWidth - paddingX * 2;
  // Valor intermedio `innerH` utilizado por la lógica de este bloque.
  const innerH = availableHeight - paddingY * 2;
  if (innerW <= 0 || innerH <= 0) return null;
  // Valor intermedio `cellW` utilizado por la lógica de este bloque.
  const cellW = (innerW - (columnGap * (cols - 1))) / cols;
  // Valor intermedio `finalCellW` utilizado por la lógica de este bloque.
  const finalCellW = Math.max(1, Math.floor(cellW));
  // Colección `cells` que reúne los elementos utilizados en este flujo.
  const cells = [];

  for (let i = 0; i < (cols * rows); i++) {
    // Valor intermedio `currentNumber` utilizado por la lógica de este bloque.
    let currentNumber;
    if (cols === 5) { 
      // Valor intermedio `gridRow` utilizado por la lógica de este bloque.
      const gridRow = Math.floor(i / cols);
      // Valor intermedio `gridCol` utilizado por la lógica de este bloque.
      const gridCol = i % cols;
      // Valor intermedio `stepsUpFromBottom` utilizado por la lógica de este bloque.
      const stepsUpFromBottom = (rows - 1) - gridRow; 
      currentNumber = Number(startCode) + (gridCol * rows) + stepsUpFromBottom;
    } else {
      currentNumber = Number(startCode) + i;
    }
    // Valor intermedio `formattedNumber` utilizado por la lógica de este bloque.
    const formattedNumber = `#${String(currentNumber).padStart(4, '0')}`;

    cells.push(
      <div key={i} style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          position: 'relative', width: `${finalCellW}px`, height: '100%', 
          transform: `rotate(${config.CUPON_ROT}deg) scale(${config.CUPON_SCALE})`,
          transformOrigin: 'center center', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <CenteredSvg url={svgUrl} showFallbackText={false} style={{ width: '100%', height: '100%' }} />
          {iconUrl && <CenteredSvg url={iconUrl} showFallbackText={false} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />}
          <div style={{
            position: 'absolute', top: '52%', left: '50%', transform: 'translate(-50%, -50%)',
            fontFamily: '"JetBrains Mono", monospace', fontSize: '7.5px', fontWeight: '900', color: '#2b3990', pointerEvents: 'none', whiteSpace: 'nowrap'
          }}>{formattedNumber}</div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ width: availableWidth, height: availableHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', transform: `translate(${offsetX}px, ${offsetY}px)`, paddingTop: `${paddingY}px`, paddingBottom: `${paddingY}px`, boxSizing: 'border-box' }}>
      <div style={{ display: 'grid', width: '100%', height: '100%', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)`, columnGap: `${columnGap}px`, rowGap: `${rowGap}px` }}>{cells}</div>
    </div>
  );
});

// =========================================================================
// 📄 COMPONENTE CORE: BOLETA FINAL
// =========================================================================
const BoletaFinalPreview = memo(function BoletaFinalPreview(props) {
    // Valores extraídos de una estructura para usarlos directamente en este bloque.
    const { boletaData, designUrls, orientation = 'vertical', cuponDesignNumber, selectedLogoNumber, showBack = false, boletaCode, screenBoletaBoost = 1, boletaScaleMultiplier = 1, backBoletaScaleMultiplier = boletaScaleMultiplier } = props;
    // Indicador booleano `isVertical` que representa la condición señalada por su nombre.
    const isVertical = (orientation || 'vertical').toLowerCase() === 'vertical';
    // Valor intermedio `currentConfig` utilizado por la lógica de este bloque.
    const currentConfig = isVertical ? CONFIG_V : CONFIG_H;
    // Permite ajustar únicamente la boleta. La matriz de cupones conserva
    // siempre su escala y distribución originales.
    const activeBoletaScaleMultiplier = showBack
      ? backBoletaScaleMultiplier
      : boletaScaleMultiplier;
    // Valor intermedio `boletaScaleV` utilizado por la lógica de este bloque.
    const boletaScaleV = currentConfig.SCALE_V * screenBoletaBoost * activeBoletaScaleMultiplier;
    // Valor intermedio `boletaScaleH` utilizado por la lógica de este bloque.
    const boletaScaleH = currentConfig.SCALE_H * screenBoletaBoost * activeBoletaScaleMultiplier;
    
    // Valor intermedio `cutLinePos` utilizado por la lógica de este bloque.
    const cutLinePos = isVertical ? CUT_LINE_X_V : CUT_LINE_X_H;

    // Valor intermedio `variantCode` utilizado por la lógica de este bloque.
    const variantCode = inferCuponVariantCode(boletaData, designUrls);
    // Valor intermedio `svgUrl` utilizado por la lógica de este bloque.
    const svgUrl = buildCuponSvgUrl(cuponDesignNumber, variantCode);
    // Valor intermedio `iconUrl` utilizado por la lógica de este bloque.
    const iconUrl = resolveIconUrl(selectedLogoNumber);
    // Valor intermedio `nativeCanon` utilizado por la lógica de este bloque.
    const nativeCanon = CANONICAL[isVertical ? 'vertical' : 'horizontal'];

    return (
      <div style={{ width: WORKSPACE_W, height: WORKSPACE_H, background: 'white', display: 'flex', position: 'relative', overflow: 'hidden' }}>
        
        {/* 🚩 LÍNEA DE RECORTE FLOTANTE: Fija del lado derecho */}
        <div style={{ 
            position: 'absolute', 
            left: `${cutLinePos}%`, 
            top: 0, bottom: 0, 
            width: '0', 
            borderLeft: '2px dashed #cbd5e1', 
            zIndex: 50, 
            pointerEvents: 'none' 
        }} />

        {/* ÁREA DE BOLETA: Fija del lado izquierdo para alinear anverso y reverso */}
        <div style={{ width: '70%', height: '100%', position: 'relative' }}>
          <div style={{ 
            position: 'absolute', top: '50%', left: '50%', 
            width: `${nativeCanon.width}px`, height: `${nativeCanon.height}px`,
            transform: buildTransform(currentConfig.BOLETA_X, currentConfig.BOLETA_Y, currentConfig.BOLETA_ROT, boletaScaleV, boletaScaleH),
            transformOrigin: 'center center'
          }}>
            {/* 🔄 SE ELIMINÓ EL GIRO DE 180°: Ahora anverso y reverso miran al mismo lado */}
            <div style={{ 
                width: '100%', height: '100%', 
                transform: 'none', 
                transformOrigin: 'center center'
            }}>
                <BoletaPreview {...props} boletaData={{...boletaData, orientation: isVertical ? 'vertical' : 'horizontal'}} />
            </div>
          </div>
        </div>

        {/* ÁREA DE CUPONES: Se oculta en el reverso, dejando el espacio en blanco de la derecha */}
        <div style={{ width: '30%', height: '100%' }}>
            {!showBack && <CuponesMatrix availableWidth={WORKSPACE_W * 0.3} availableHeight={WORKSPACE_H} svgUrl={svgUrl} iconUrl={iconUrl} config={currentConfig} startCode={boletaCode || 0} offsetX={currentConfig.CUPONES_X} offsetY={currentConfig.CUPONES_Y || 0} />}
        </div>
      </div>
    );
});

// =========================================================================
// 🖥️ UI PREVIEW SCALED (LA WEB)
// =========================================================================
export const BoletaFinalPreviewScaled = memo(function BoletaFinalPreviewScaled(props) {
    // Referencia mutable `outerRef` que conserva datos entre renderizados sin provocar uno nuevo.
    const outerRef = useRef(null);
    // Estado local `scale` y su actualizador, usados para reflejar cambios en la interfaz.
    const [scale, setScale] = useState(1);

    // Indicador booleano `isHorizontal` que representa la condición señalada por su nombre.
    const isHorizontal = (props.orientation || props.boletaData?.orientation) === 'horizontal';

    // Callback `measure` memoizado para conservar su referencia entre renderizados.
    const measure = useCallback(() => {
      if (!outerRef.current) return;
      // offsetWidth/offsetHeight no se ven afectados por el transform: scale()
      // del lienzo padre: así conserva un tamaño real y estable dentro de la distribución.
      const w = outerRef.current.offsetWidth;
      // Valor intermedio `h` utilizado por la lógica de este bloque.
      const h = outerRef.current.offsetHeight;
      // Valor intermedio `sW` utilizado por la lógica de este bloque.
      const sW = (w - 20) / WORKSPACE_W;
      // Valor intermedio `sH` utilizado por la lógica de este bloque.
      const sH = (h - 20) / WORKSPACE_H;
      setScale(Math.min(sW, sH));
    }, []);

    useEffect(() => {
      measure();
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }, [measure]);

    return (
      <div ref={outerRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#e2e8f0', position: 'relative' }}>
        <div style={{ width: WORKSPACE_W, height: WORKSPACE_H, transform: `scale(${scale})`, transformOrigin: 'center center', boxShadow: '0 20px 50px rgba(0,0,0,0.15)', flexShrink: 0 }}>
            <BoletaFinalPreview {...props} previewTitleNudge={isHorizontal ? 5 : 0} screenBoletaBoost={isHorizontal ? 1.25 : 1} />
        </div>
        <BoletaTabloidPrintTemplate {...props} />
      </div>
    );
});

// =========================================================================
// 🖨️ PREVIEW STANDALONE DE LA BOLETA (sólo la boleta, sin cupones)
// Reusa el MISMO escalado de impresión (scale no-uniforme) y el MISMO recorte
// vertical del tabloide, de modo que la boleta se ve exactamente como al
// imprimir (incluida la posición del título), pero:
//   • SIN cupones (no renderiza el área derecha)
//   • SIN rotación (la horizontal se ve derecha, no girada como en el tabloide)
//   • Centrada (sin el desplazamiento horizontal del tabloide)
// =========================================================================
export const BoletaPrintAreaPreview = memo(function BoletaPrintAreaPreview({
  boletaData, zoom = 1, style = {}, ...props
}) {
  // Referencia mutable `outerRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const outerRef = useRef(null);
  // Estado local `size` y su actualizador, usados para reflejar cambios en la interfaz.
  const [size, setSize] = useState({ w: 0, h: 0 });

  // Callback `measure` memoizado para conservar su referencia entre renderizados.
  const measure = useCallback(() => {
    if (!outerRef.current) return;
    // Valor intermedio `w` utilizado por la lógica de este bloque.
    const w = outerRef.current.offsetWidth;
    // Valor intermedio `h` utilizado por la lógica de este bloque.
    const h = outerRef.current.offsetHeight;
    setSize((prev) => (Math.abs(prev.w - w) < 1 && Math.abs(prev.h - h) < 1 ? prev : { w, h }));
  }, []);

  useEffect(() => {
    measure();
    // Valor intermedio `ro` utilizado por la lógica de este bloque.
    const ro = new ResizeObserver(measure);
    if (outerRef.current) ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [measure]);

  // Valor intermedio `orientation` utilizado por la lógica de este bloque.
  const orientation = boletaData?.orientation || 'vertical';
  // Indicador booleano `isVertical` que representa la condición señalada por su nombre.
  const isVertical = orientation === 'vertical';
  // Valor intermedio `cfg` utilizado por la lógica de este bloque.
  const cfg = isVertical ? CONFIG_V : CONFIG_H;
  // Valor intermedio `canon` utilizado por la lógica de este bloque.
  const canon = CANONICAL[orientation];

  // Mismo aspect del área de boleta en el tabloide (70% ancho × 100% alto),
  // así el escalado y el recorte vertical coinciden con la impresión.
  const AREA_W = WORKSPACE_W * 0.7;
  // Constante `AREA_H` que centraliza un valor de configuración compartido.
  const AREA_H = WORKSPACE_H;

  // Valor intermedio `fitScale` utilizado por la lógica de este bloque.
  let fitScale = 0;
  if (size.w > 0 && size.h > 0) {
    fitScale = Math.min(size.w / AREA_W, size.h / AREA_H) * zoom;
  }

  return (
    <div ref={outerRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', ...style }}>
      {fitScale > 0 && (
        <div style={{ width: AREA_W * fitScale, height: AREA_H * fitScale, position: 'relative', overflow: 'hidden' }}>
          <div style={{ width: AREA_W, height: AREA_H, transformOrigin: 'top left', transform: `scale(${fitScale})`, position: 'relative' }}>
            {/* Wrapper de la boleta: escala de impresión, centrado, SIN rotación, SIN cupones */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${canon.width}px`,
                height: `${canon.height}px`,
                transform: `translate(-50%, -50%) scale(${cfg.SCALE_H}, ${cfg.SCALE_V})`,
                transformOrigin: 'center center',
              }}
            >
              <BoletaPreview {...props} boletaData={{ ...boletaData, orientation }} previewTitleNudge={!isVertical ? 5 : 0} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// =========================================================================
// 🖨️ PORTAL DE IMPRESIÓN (PDF)
// =========================================================================
export const BoletaTabloidPrintTemplate = memo(function BoletaTabloidPrintTemplate(props) {
  // Valores extraídos de una estructura para usarlos directamente en este bloque.
  const { boletaData, boletaCode, printFileId } = props;
  useEffect(() => {
    // Valor intermedio `previousTitle` utilizado por la lógica de este bloque.
    const previousTitle = document.title;
    // Valor intermedio `requestedTitle` utilizado por la lógica de este bloque.
    const requestedTitle = printFileId
      ? String(printFileId)
      : boletaData?.companyName && boletaCode
        ? `${String(boletaCode).padStart(4, '0')}_${boletaData.companyName.trim().replace(/\s+/g, '_')}`
        : '';
    // Valor intermedio `safeTitle` utilizado por la lógica de este bloque.
    const safeTitle = requestedTitle.replace(/[\\/:*?"<>|]/g, '_').trim();

    if (safeTitle) document.title = safeTitle;

    return () => {
      document.title = previousTitle;
    };
  }, [boletaData, boletaCode, printFileId]);

  return ReactDOM.createPortal(
    <div className="wisby-pure-print-template">
      <style>{`
        @media screen { .wisby-pure-print-template { display: none !important; } }
        @media print {
          body > *:not(.wisby-pure-print-template) { display: none !important; }
          @page { size: 17in 11in landscape; margin: 0 !important; }
          html, body { background: white !important; height: 11in; width: 17in; overflow: hidden !important; -webkit-print-color-adjust: exact; }
          .print-sheet { position: relative !important; width: 17in !important; height: 11in !important; display: flex !important; page-break-after: always !important; }
          
          /* 👇 NUEVA REGLA: OCULTAR ONDAS SOLO EN IMPRESIÓN 👇 */
          img[src$="/3.svg"],
          img[src$="/3_H.svg"],
          img[src$="/6.svg"] {
            display: none !important;
            opacity: 0 !important;
            visibility: hidden !important;
          }

          /* 🛡️ TRUCO MAESTRO: AJUSTE MANUAL DEL ESCUDO SOLO EN EL PDF */
          .wisby-pure-print-template .is-horizontal .reverso-content-wrapper > div:first-child {
            transform: translate(calc(-50% + ${PRINT_SHIELD_ADJUST_H.X}px), calc(-50% + ${PRINT_SHIELD_ADJUST_H.Y}px)) rotate(0deg) !important;
          }
          .wisby-pure-print-template .is-vertical .reverso-content-wrapper > div:first-child {
            transform: translate(calc(-50% + ${PRINT_SHIELD_ADJUST_V.X}px), calc(-50% + ${PRINT_SHIELD_ADJUST_V.Y}px)) rotate(-90deg) !important;
          }
        }
      `}</style>
      <div className="print-sheet"><BoletaFinalPreview {...props} showBack={false} /></div>
      <div className="print-sheet"><BoletaFinalPreview {...props} showBack={true} /></div>
    </div>,
    document.body
  );
});

export default memo(BoletaFinalPreview);
